const WIND_FORECAST_CAP = 25
const WIND_FORECAST_MAX_AGE_MS = 30 * 60 * 1000
const WIND_FORECAST_IDLE_MS = 750
const DEFAULT_WIND_TOLERANCE_DEG = 45

function isWindSet (directionDeg) {
  return typeof directionDeg === 'number' &&
    Number.isInteger(directionDeg) &&
    directionDeg >= 0 &&
    directionDeg <= 359
}

function effectiveToleranceDeg (directionDeg, toleranceDeg) {
  if (
    typeof toleranceDeg === 'number' &&
    Number.isInteger(toleranceDeg) &&
    toleranceDeg >= 1 &&
    toleranceDeg <= 180
  ) {
    return toleranceDeg
  }
  return DEFAULT_WIND_TOLERANCE_DEG
}

function circularDifferenceDeg (a, b) {
  let difference = Math.abs(a - b) % 360
  if (difference > 180) difference = 360 - difference
  return difference
}

function matchWind (directionDeg, toleranceDeg, forecastDirectionDeg, coverage) {
  if (coverage === 'not_forecasted') return 'not_forecasted'
  if (
    coverage === 'unavailable' ||
    typeof forecastDirectionDeg !== 'number' ||
    !Number.isFinite(forecastDirectionDeg)
  ) {
    return 'unavailable'
  }
  if (!isWindSet(directionDeg)) return 'unset'
  if (circularDifferenceDeg(directionDeg, forecastDirectionDeg) <= effectiveToleranceDeg(directionDeg, toleranceDeg)) {
    return 'match'
  }
  return 'poor'
}

function solarNoonUnix (sunriseUnix, sunsetUnix) {
  if (!Number.isFinite(sunriseUnix) || !Number.isFinite(sunsetUnix) || sunsetUnix <= sunriseUnix) return null
  return sunriseUnix + Math.floor((sunsetUnix - sunriseUnix) / 2)
}

function nearestHourIndex (timesUnix, targetUnix, directions) {
  const times = Array.isArray(timesUnix) ? timesUnix : []
  const dirs = Array.isArray(directions) ? directions : []
  let best = null
  for (let index = 0; index < times.length; index += 1) {
    const direction = dirs[index]
    if (typeof direction !== 'number' || !Number.isFinite(direction)) continue
    const time = times[index]
    if (typeof time !== 'number' || !Number.isFinite(time)) continue
    const distance = Math.abs(time - targetUnix)
    const closer = !best || distance < best.distance
    const earlierTie = best && distance === best.distance && time < best.time
    if (closer || earlierTie) best = { index, distance, time }
  }
  return best ? best.index : null
}

function markersInInclusiveBounds (markers, bounds) {
  if (!bounds) return markers
  const south = bounds.sw && bounds.sw.lat
  const west = bounds.sw && bounds.sw.lng
  const north = bounds.ne && bounds.ne.lat
  const east = bounds.ne && bounds.ne.lng
  return (markers || []).filter((marker) =>
    marker.lat >= south &&
    marker.lat <= north &&
    marker.long >= west &&
    marker.long <= east
  )
}

function forecastCandidateIds (rows, cap) {
  const sorted = (rows || []).slice().sort((left, right) => {
    const titleA = String(left.title || '').trim().toLocaleLowerCase('en-US')
    const titleB = String(right.title || '').trim().toLocaleLowerCase('en-US')
    const byTitle = titleA.localeCompare(titleB, 'en-US')
    if (byTitle !== 0) return byTitle
    return String(left.id).localeCompare(String(right.id), 'en-US')
  })
  const limit = typeof cap === 'number' && cap > 0 ? cap : 0
  return {
    forecasted: sorted.slice(0, limit).map((row) => row.id),
    notForecasted: sorted.slice(limit).map((row) => row.id)
  }
}

function parseWholeDirection (value) {
  if (value === '' || value === null || value === undefined) return { ok: false, empty: true }
  const number = typeof value === 'number' ? value : Number(String(value).trim())
  if (!Number.isFinite(number) || !Number.isInteger(number)) return { ok: false, empty: false }
  if (number === 360) return { ok: true, empty: false, direction: 0 }
  if (number < 0 || number > 359) return { ok: false, empty: false }
  return { ok: true, empty: false, direction: number }
}

function restWindWrite (baselineDirection, baselineTolerance, direction, tolerance, cleared) {
  if (cleared) {
    return { OptimalWindDirection: null, OptimalWindTolerance: null }
  }
  if (!isWindSet(direction)) return undefined
  const nextTolerance = effectiveToleranceDeg(direction, tolerance)
  if (
    isWindSet(baselineDirection) &&
    baselineDirection === direction &&
    effectiveToleranceDeg(baselineDirection, baselineTolerance) === nextTolerance
  ) {
    return undefined
  }
  return { OptimalWindDirection: direction, OptimalWindTolerance: nextTolerance }
}

function validateWindDraft (draft) {
  const source = draft || {}
  if (source.cleared) {
    return {
      ok: true,
      error: '',
      write: { OptimalWindDirection: null, OptimalWindTolerance: null }
    }
  }
  const parsed = parseWholeDirection(source.direction)
  if (parsed.empty) return { ok: true, error: '', write: undefined }
  if (!parsed.ok) {
    return {
      ok: false,
      error: 'Comes from must be a whole number from 0 through 359.',
      write: undefined
    }
  }
  const toleranceNumber = typeof source.tolerance === 'number' ? source.tolerance : Number(source.tolerance)
  if (!Number.isInteger(toleranceNumber) || toleranceNumber < 1 || toleranceNumber > 180) {
    return {
      ok: false,
      error: 'Tolerance must be a whole number from 1 through 180.',
      write: undefined
    }
  }
  return {
    ok: true,
    error: '',
    write: restWindWrite(
      source.baselineDirection,
      source.baselineTolerance,
      parsed.direction,
      toleranceNumber,
      false
    )
  }
}

function markerDeleted (marker) {
  if (!marker) return false
  const value = marker.deleted != null
    ? marker.deleted
    : (marker.is_deleted != null
      ? marker.is_deleted
      : (marker.isDeleted != null ? marker.isDeleted : marker.IsDeleted))
  return value === true || value === 1 || value === '1'
}

function selectPlanningMarkers (markers, onScreenOnly, bounds) {
  const active = []
  for (const marker of markers || []) {
    if (!marker || markerDeleted(marker) || marker.id == null || marker.id === '') continue
    active.push(marker)
  }
  if (!onScreenOnly) return active
  if (!bounds) return []
  const located = active.filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.long))
  return markersInInclusiveBounds(located, bounds)
}

module.exports = {
  WIND_FORECAST_CAP,
  WIND_FORECAST_MAX_AGE_MS,
  WIND_FORECAST_IDLE_MS,
  DEFAULT_WIND_TOLERANCE_DEG,
  isWindSet,
  effectiveToleranceDeg,
  circularDifferenceDeg,
  matchWind,
  solarNoonUnix,
  nearestHourIndex,
  markersInInclusiveBounds,
  forecastCandidateIds,
  parseWholeDirection,
  restWindWrite,
  validateWindDraft,
  selectPlanningMarkers
}
